import { useState, useEffect } from 'react';
import { AgentAvatar, PALETTES, parseSeed } from '../AgentAvatar';
import { useWallet } from '../../hooks/useWallet';
import { useModalAlert } from '../ModalAlert';

const SLUG_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

function makeSeed(name, paletteIndex) {
  const rand = `${name}-${Math.random().toString(36).slice(2, 8)}`;
  return `${rand}|${paletteIndex}`;
}

function randomPaletteIndex(currentIndex) {
  return (currentIndex + 1 + Math.floor(Math.random() * (PALETTES.length - 1))) % PALETTES.length;
}

/**
 * Agent identity editor — name, model, max history, avatar picker.
 * Model selection here is the single source of truth; changes cascade
 * to all research configs and prompts on the backend.
 */
export function AgentIdentityEditor({ agent, config, onSave }) {
  const { jwtToken } = useWallet();
  const { showSuccess, showError } = useModalAlert();
  const apiBase = config?.apiBaseUrl || 'http://localhost:8000';

  const [editName, setEditName] = useState('');
  const [editMaxHistory, setEditMaxHistory] = useState(10);
  const [editAvatarSeed, setEditAvatarSeed] = useState(null);
  const [editAvatarPalette, setEditAvatarPalette] = useState(PALETTES[0]);
  const [editModelName, setEditModelName] = useState('');
  const [modelOptions, setModelOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  // Initialize fields from agent prop
  useEffect(() => {
    if (!agent) return;
    setEditName(agent.name || '');
    setEditMaxHistory(agent.max_history_messages ?? 10);
    setEditAvatarSeed(agent.avatar_seed || null);
    setEditModelName(agent.model_name || '');
    const { paletteIndex } = parseSeed(agent.avatar_seed);
    setEditAvatarPalette(PALETTES[paletteIndex] || PALETTES[0]);
  }, [agent]);

  // Fetch model options filtered by the authenticated user's subscription plan.
  useEffect(() => {
    if (!jwtToken) return;
    const load = async () => {
      try {
        const res = await fetch(`${apiBase}/models`, {
          headers: { Authorization: `Bearer ${jwtToken}` },
        });
        const models = res.ok ? await res.json() : [];
        setModelOptions(models.map((m) => ({ value: m.id, label: m.display_name || m.id })));
      } catch (err) {
        console.error('Failed to load model options:', err);
      }
    };
    load();
  }, [apiBase, jwtToken]);

  const selectModelOptions = editModelName && !modelOptions.some((opt) => opt.value === editModelName)
    ? [{ value: editModelName, label: editModelName }, ...modelOptions]
    : modelOptions;

  const handleRegen = () => {
    const { paletteIndex: currentPI } = parseSeed(editAvatarSeed || agent?.avatar_seed);
    const nextPI = randomPaletteIndex(currentPI);
    const newSeed = makeSeed(editName || agent?.name || 'agent', nextPI);
    setEditAvatarSeed(newSeed);
    setEditAvatarPalette(PALETTES[nextPI] || PALETTES[0]);
  };

  const handleSave = async () => {
    if (!agent || saving || !SLUG_RE.test(editName)) return;
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/agents/${agent.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editName,
          max_history_messages: editMaxHistory,
          avatar_seed: editAvatarSeed,
          model_name: editModelName || undefined,
        }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      showSuccess('Agent Updated', 'Identity saved successfully.', { autoClose: true });
      if (onSave) onSave();
    } catch (err) {
      showError('Save Failed', err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!agent) return null;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm text-gray-400 mb-1">Agent Name</label>
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          className={`w-full bg-gray-800 border rounded px-3 py-2 text-white text-sm focus:outline-none font-mono ${
            editName && !SLUG_RE.test(editName)
              ? 'border-red-500 focus:border-red-500'
              : 'border-gray-700 focus:border-cyan-500'
          }`}
        />
        {editName && !SLUG_RE.test(editName) && (
          <p className="text-xs text-red-400 mt-1">Lowercase letters, numbers, and hyphens only</p>
        )}
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-1">LLM Model</label>
        <select
          value={editModelName}
          onChange={(e) => setEditModelName(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500"
        >
          {selectModelOptions.length === 0 ? (
            <option value="">Loading models...</option>
          ) : (
            selectModelOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))
          )}
        </select>
        <p className="text-xs text-slate-500 mt-1">
          Used for both research and recommendations. Changing this updates all configs.
        </p>
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-1">Max History Messages</label>
        <input
          type="number"
          min={1}
          max={100}
          value={editMaxHistory}
          onChange={(e) => setEditMaxHistory(parseInt(e.target.value, 10) || 10)}
          className="w-32 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Avatar</label>
        <div className="flex items-center gap-3">
          <AgentAvatar
            name={editName || agent.name}
            seed={parseSeed(editAvatarSeed || agent.avatar_seed).avatarSeed || (editName || agent.name)}
            colors={editAvatarPalette}
            size={56}
          />
          <button
            type="button"
            onClick={handleRegen}
            title="Generate new avatar"
            className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving || !SLUG_RE.test(editName)}
        className={`w-full py-2 px-4 rounded text-white text-sm font-medium transition-colors ${
          saving || !SLUG_RE.test(editName)
            ? 'bg-cyan-900 opacity-50 cursor-not-allowed'
            : 'bg-cyan-600 hover:bg-cyan-500 cursor-pointer'
        }`}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}
